import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography, Skeleton } from '@mui/material'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ReferenceLine, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts'
import { useQuery } from '@apollo/client'
import CCTVDiskTooltip from './tooltips/CCTVDiskTooltip'
import { CCTV_SERVER_DISK_GRAPH } from '../../../operations-gql/queries'
import { formatGraphTimeTick, getGraphTimeTicks } from '../CompleteDetailView/utils'

const DISK_1_COLOR = '#3B82F6'
const DISK_2_COLOR = '#F59E0B'

const axisStyle = {
  fontSize: 10,
  fill: '#A2AAB8',
}

interface DiskInfo {
  volume: string
  status: string
  free_space: number
  capacity: number
}

interface DisksInfo {
  [key: string]: DiskInfo
}

interface GraphDataPoint {
  timestamp: string
  timestampMs: number
  time: string
  formattedTime: string
  disk_1?: number
  disk_2?: number
}

interface CCTVDiskGraphProps {
  device: {
    mac: string
    disks_info?: DisksInfo | Record<string, unknown> | null
  }
}

const formatYAxis = (value: number) => `${value.toFixed(0)}%`

const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}

const formatTimeForDisplay = (timestamp: string | number | Date, locale: string): string => {
  const date = timestamp instanceof Date ? timestamp : new Date(typeof timestamp === 'number' && timestamp < 10000000000 ? timestamp * 1000 : timestamp)
  if (isNaN(date.getTime())) return ''

  const formatted = date.toLocaleString(locale, {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })

  return capitalizeFirstLetter(formatted)
}

const CCTVDiskGraph: React.FC<CCTVDiskGraphProps> = ({ device }) => {
  const { t, i18n } = useTranslation()
  const [timeRange] = React.useState('-1d')

  const { loading, data } = useQuery(CCTV_SERVER_DISK_GRAPH, {
    variables: {
      mac: device.mac,
      timeRange: { preset: timeRange }
    },
    fetchPolicy: 'network-only',
    skip: !device.mac
  })

  const availableDisks = useMemo(() => {
    if (!device.disks_info) {
      return []
    }
    const typedDisksInfo = device.disks_info as DisksInfo
    return Object.keys(typedDisksInfo).filter(key => typedDisksInfo[key])
  }, [device.disks_info])

  const graphData = useMemo(() => {
    if (!data?.cctvServerGraph) return []

    const { timestamps, disk_1_usage, disk_2_usage } = data.cctvServerGraph

    return timestamps.map((timestamp: string, index: number) => {
      const point: GraphDataPoint = {
        timestamp,
        timestampMs: Number(timestamp) < 10000000000 ? Number(timestamp) * 1000 : new Date(timestamp).getTime(),
        time: timestamp,
        formattedTime: formatTimeForDisplay(timestamp, i18n.language),
      }

      if (availableDisks.includes('1')) {
        point.disk_1 = disk_1_usage[index] || 0
      }
      if (availableDisks.includes('2')) {
        point.disk_2 = disk_2_usage[index] || 0
      }

      return point
    })
  }, [data, i18n.language, availableDisks])

  const displayData = graphData

  const diskLabels = useMemo<{ disk_1?: string; disk_2?: string }>(() => {
    if (!device.disks_info) {
      return {}
    }

    const typedDisksInfo = device.disks_info as DisksInfo
    const labels: { disk_1?: string; disk_2?: string } = {}

    if (typedDisksInfo['1']) {
      labels.disk_1 = typedDisksInfo['1'].volume || 'Disk 1'
    }
    if (typedDisksInfo['2']) {
      labels.disk_2 = typedDisksInfo['2'].volume || 'Disk 2'
    }

    return labels
  }, [device.disks_info])
  const timeTicks = useMemo(() => getGraphTimeTicks(displayData), [displayData])
  const latestPoint = displayData[displayData.length - 1]

  if (!device.disks_info || availableDisks.length === 0) return null

  if (loading) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h1Card" sx={{ mb: 2 }}>
          {t('supervision.cctv.disk_usage')}
        </Typography>
        <Box sx={{ border: '1px solid #EFEFEF', borderRadius: '12px', p: 2 }}>
          <Skeleton variant="rectangular" width="100%" height={200} />
        </Box>
      </Box>
    )
  }

  if (!displayData.length) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h1Card" sx={{ mb: 2 }}>
          {t('supervision.cctv.disk_usage')}
        </Typography>
        <Box sx={{ border: '1px solid #EFEFEF', borderRadius: '12px', p: 2 }}>
          <Box sx={{
            height: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="body2" color="text.secondary">
              {t('wan.graphs.no_data')}
            </Typography>
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h1Card" sx={{ mb: 2 }}>
        {t('supervision.cctv.disk_usage')}
      </Typography>
      <Box sx={{ border: '1px solid #EFEFEF', borderRadius: '12px', p: 2 }}>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart
            data={displayData}
            margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="timestampMs"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              ticks={timeTicks}
              tickFormatter={timestamp => formatGraphTimeTick(timestamp, displayData)}
              interval={0}
              minTickGap={16}
              axisLine={{ stroke: '#EFEFEF' }}
              tick={{ ...axisStyle }}
            />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 50, 100]}
              tickFormatter={formatYAxis}
              tick={{ ...axisStyle }}
            />
            <ReferenceLine y={0} stroke="#EFEFEF" strokeWidth={1} />
            <ReferenceLine y={50} stroke="#EFEFEF" strokeWidth={1} />
            <RechartsTooltip content={<CCTVDiskTooltip availableDisks={availableDisks} />} />
            {diskLabels.disk_1 && (
              <Area
                type="monotone"
                dataKey="disk_1"
                name={diskLabels.disk_1}
                fill="#E4EEFA"
                stroke={DISK_1_COLOR}
                strokeWidth={2}
                isAnimationActive={false}
              />
            )}
            {diskLabels.disk_2 && (
              <Area
                type="monotone"
                dataKey="disk_2"
                name={diskLabels.disk_2}
                fill="#FEF4E9"
                stroke={DISK_2_COLOR}
                strokeWidth={2}
                isAnimationActive={false}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          mt: 1,
          mr: 3,
          fontSize: 10
        }}>
          {diskLabels.disk_1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{
                width: 13,
                height: 13,
                borderRadius: '4px',
                backgroundColor: DISK_1_COLOR
              }} />
              <Typography sx={{ fontSize: 10, color: '#666' }}>
                {diskLabels.disk_1}{latestPoint?.disk_1 != null ? ` : ${latestPoint.disk_1.toFixed(1)}%` : ''}
              </Typography>
            </Box>
          )}

          {diskLabels.disk_2 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{
                width: 13,
                height: 13,
                borderRadius: '4px',
                backgroundColor: DISK_2_COLOR
              }} />
              <Typography sx={{ fontSize: 10, color: '#666' }}>
                {diskLabels.disk_2}{latestPoint?.disk_2 != null ? ` : ${latestPoint.disk_2.toFixed(1)}%` : ''}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default CCTVDiskGraph